import React from 'react';
import { Button, useToasts } from 'freemium-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import {
  AccountInfoType,
  CollectorStateType,
  CollectorType,
  HostConfigType,
  SurveyType,
  UserType,
} from 'shared/types';
import { SaveCollectorType, SubmitOptionType } from '../../action/types';
import {
  useCollectorStateChange,
  useSaveCollector,
} from '../../action/collector/hooks';
import {
  getCollectorPayload,
  getCollectorSchema,
} from '../../action/collector/utils';
import { useSurveyStateChange } from '../../action/hooks';
import AudienceScheduleConfirmation from '../../action/components/audience-schedule-confirmation';
import { getStateChangeToastText } from '../../action/utils';
import { CollectorDataType } from '../types';
import {
  CollectorNewStateType,
  CollectorStateChangeType,
} from '../../action/collector/types';
import { useEmailVerifyCxt } from 'shared/email-verify-provider';
import { getInfoText } from 'shared/utils';
import { useProductConfigContext } from 'shared/product-config-provider';

type ScListActivateButtonProps = {
  survey: SurveyType;
  disabled?: boolean;
  collectorData?: CollectorDataType[];
  setCollectorData?: React.Dispatch<React.SetStateAction<CollectorDataType[]>>;
  isLoading?: boolean;
  activeSurveyCount: number;
  collector: CollectorType;
  accountInfo: AccountInfoType;
  userInfo: UserType;
  hostConfig: HostConfigType;
};

const newStateMap: Record<CollectorNewStateType, CollectorStateType> = {
  activate: 'ACTIVE',
  complete: 'COMPLETE',
  pause: 'PAUSE',
};

export const ScListActivateButton = ({
  survey,
  collector,
  disabled,
  isLoading,
  collectorData = [],
  hostConfig = {},
  activeSurveyCount,
  userInfo,
  setCollectorData,
  accountInfo,
}: ScListActivateButtonProps) => {
  const [, setToast] = useToasts();
  const [showActivateConfirmation, toggleActivateConfirmation] =
    React.useState<boolean>(false);
  const {
    mutateAsync: surveyStateChangeAsync,
    isLoading: isLoadingSurveyStateChange,
  } = useSurveyStateChange({
    setToast,
  });
  const [submitTime, setSubmitTime] = React.useState<SubmitOptionType>('');

  const productConfig = useProductConfigContext();
  const { verifiedUsers } = useEmailVerifyCxt();
  const {
    mutate: collectorStateChange,
    isLoading: isLoadingCollectorStateChange,
  } = useCollectorStateChange({
    callback: (
      isSuccess: boolean,
      response: any,
      data: CollectorStateChangeType,
    ) => {
      if (isSuccess) {
        setToast({
          type: 'success',
          delay: 3000,
          text: getStateChangeToastText({
            collector: data.collector,
            submitTime,
            singleChannelMode: true,
            timezone: accountInfo?.timezone || '',
          }),
        });
        toggleActivateConfirmation(false);
        setSubmitTime('');
        if (setCollectorData) {
          const newCollectorData = collectorData.map(
            (collectorData: CollectorDataType) => {
              if (collectorData.id === survey.id) {
                return {
                  ...collectorData,
                  collectors: collectorData.collectors.map(
                    (col: CollectorType): CollectorType =>
                      col.id === data.collectorId
                        ? {
                            ...col,
                            state: newStateMap[data.newState],
                          }
                        : col,
                  ),
                };
              }
              return collectorData;
            },
          );
          setCollectorData(newCollectorData);
        }
      } else {
        setToast({
          type: 'error',
          delay: 3000,
          text: 'Survey activation failed',
        });
      }
    },
  });
  const { mutate: saveCollector, isLoading: isLoadingSaveCollector } =
    useSaveCollector({
      setToast,
      callback: async (
        isSuccess: boolean,
        response: CollectorType,
        { activate }: SaveCollectorType,
      ) => {
        if (isSuccess) {
          if (activate) {
            if (survey.id && survey?.state && survey.state !== 'ACTIVE') {
              // In SC we need to activate the survey during collector activate
              // Activate button will be disabled if the active survey limit is reached
              // So need not show a toast here
              await surveyStateChangeAsync({
                surveyId: survey.id,
                newState: 'activate',
                survey,
              });
            }
            collectorStateChange({
              surveyId: survey?.id || '',
              collectorId: response?.id || '',
              newState: 'activate',
              collector: response,
            });
          }
        }
      },
    });

  const collectorForm = useForm<CollectorType>({
    shouldFocusError: true,
    resolver: yupResolver(
      getCollectorSchema({
        channel: collector.channel || 'EMAIL',
        stepChannel: 'AUDIENCE_SCHEDULE',
      }),
    ),
    defaultValues: collector,
  });
  const from_email =
    collectorForm.watch('channel_info.email_channel_info.from_email') || '';
  const reply_to =
    collectorForm.watch('channel_info.email_channel_info.reply_to') || '';

  const infoTextForAccount = React.useMemo<string>(
    () =>
      getInfoText({
        userInfo,
        accountInfo,
        productConfig,
        activeSurveyCount,
        hostConfig,
      }),
    [accountInfo, userInfo, hostConfig?.limits, activeSurveyCount],
  );

  const infoText = React.useMemo<string>(
    () =>
      getInfoText({
        verifiedUsers,
        collector,
        productConfig,
        from_email,
        reply_to,
      }),
    [verifiedUsers, collector, from_email, reply_to],
  );

  return (
    <Button
      disabled={
        disabled ||
        isLoading ||
        isLoadingCollectorStateChange ||
        isLoadingSurveyStateChange ||
        isLoadingSaveCollector ||
        !!infoTextForAccount
      }
      className="actionbtn"
      size="mini"
      type="secondary"
      inline
      onClick={() => {
        if (infoText) {
          setToast({
            text: infoText,
            type: 'warning',
            delay: 40000,
          });
          return;
        }
        toggleActivateConfirmation(true);
      }}
    >
      Activate
      <FormProvider {...collectorForm}>
        <AudienceScheduleConfirmation
          isLoading={
            isLoading ||
            isLoadingCollectorStateChange ||
            isLoadingSaveCollector ||
            isLoadingSurveyStateChange
          }
          dialogType="survey"
          accountInfo={accountInfo || {}}
          showConfirmation={showActivateConfirmation}
          toggleConfirmation={toggleActivateConfirmation}
          onSubmit={(submitTimeOpt: SubmitOptionType) => {
            if (Object.keys(collectorForm.formState.errors).length) return;
            setSubmitTime(submitTimeOpt);
            saveCollector({
              payload: {
                ...collector,
                ...getCollectorPayload({
                  values: collectorForm.getValues(),
                  stepChannel: 'AUDIENCE_SCHEDULE',
                  channel: collector.channel || 'EMAIL',
                  surveyId: survey?.id || '',
                }),
              },
              surveyId: survey?.id || '',
              activate: true,
              singleChannelMode: true,
            });
          }}
        />
      </FormProvider>
    </Button>
  );
};

export default ScListActivateButton;
